/**
 * "Record scenario" in a live session: the hub records what the person does until they stop, then the
 * steps are named and saved as a scenario.
 *
 * The recording itself happens on the hub (`record:start` / `record:stop` on the session) — only the
 * hub can read the screen under each tap, which is what lets a replay find the same button on another
 * phone. While recording, taps arrive a little late (the hub dumps the screen first), and the hint says
 * so rather than letting it read as lag.
 *
 * A recording that contains typed text is flagged before saving: whatever was typed will be typed
 * verbatim on every phone, and a caption is the thing nobody wants repeated a hundred times.
 */

import { Circle, Square } from 'lucide-react'
import { useState } from 'react'

import type { ScenarioStep } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { toast } from '../../lib/toast'
import { Badge } from '../../ui/components'

export function RecordControl({ sessionId, deviceId, rail = false }: {
  sessionId: string | null
  deviceId: string
  /** Drawn as a button of the phone's action rail (icon over label), not an inline button. */
  rail?: boolean
}) {
  const { api } = useApp()
  const t = useT()
  const [recording, setRecording] = useState(false)
  const [busy, setBusy] = useState(false)
  const [steps, setSteps] = useState<ScenarioStep[] | null>(null)
  const [name, setName] = useState('')

  const toggle = async () => {
    if (!sessionId) return
    setBusy(true)
    try {
      if (!recording) {
        await api.recordStart(sessionId)
        setRecording(true)
      } else {
        const stopped = await api.recordStop(sessionId)
        setRecording(false)
        if (stopped.steps.length === 0) toast(t.session.recordEmpty, 'bad')
        else {
          toast(t.session.recordStopped(stopped.steps.length))
          setSteps(stopped.steps)
        }
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!steps || !name.trim()) return
    setBusy(true)
    try {
      await api.createScenario({ name: name.trim(), steps, recorded_on: deviceId })
      toast(t.scenarios.saved(name.trim()))
      setSteps(null)
      setName('')
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    } finally {
      setBusy(false)
    }
  }

  const hasText = Boolean(steps?.some((s) => s.action === 'text'))

  return (
    <>
      <button
        className={`${rail ? 'rail-btn' : ''}${recording ? ' danger' : ''}`.trim()}
        aria-pressed={recording}
        disabled={!sessionId || busy}
        title={t.session.recordHint}
        aria-label={recording ? t.session.recording : t.session.record}
        onClick={() => void toggle()}
      >
        {recording ? <Square className="ico" size={rail ? 18 : 13} aria-hidden /> : <Circle className="ico" size={rail ? 18 : 13} aria-hidden />}
        <span>{recording ? t.session.recording : t.session.record}</span>
      </button>
      {recording && !rail && <span className="tiny dim">{t.session.recordHint}</span>}

      {steps && (
        <div className="scrim" role="dialog" aria-modal="true">
          <div className="dialog" style={{ maxWidth: 520 }}>
            <h3>{t.session.recordSaveTitle}</h3>
            <p className="dim small">{t.scenarios.steps(steps.length)}</p>
            <ol className="dim small" style={{ maxHeight: 200, overflow: 'auto', paddingLeft: '1.4em' }}>
              {steps.map((s, i) => (
                <li key={i}>
                  {t.scenarios.stepKinds[s.action] ?? s.action}
                  {s.target?.text || s.target?.content_desc ? ` · “${s.target.text || s.target.content_desc}”` : ''}
                  {s.action === 'text' ? ` · “${String(s.text)}”` : ''}
                  {s.action === 'key' ? ` · ${String(s.key)}` : ''}
                  {s.pause_s ? ` · +${s.pause_s}s` : ''}
                </li>
              ))}
            </ol>
            {hasText && <Badge kind="warn">{t.session.recordTextWarning}</Badge>}
            <input
              className="grow"
              placeholder={t.session.recordName}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save()
              }}
            />
            <div className="actions">
              <button className="ghost" disabled={busy} onClick={() => setSteps(null)}>
                {t.session.recordDiscard}
              </button>
              <button className="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
                {t.common.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
